import { useActiveChatId } from "@/lib/hooks/useActiveChatId";

import { FileCode2 } from "lucide-react";

import type { FpMessagePart } from "../../../../../schemas/codegen";
import {
  ExpandableMessageCard,
  FileName,
  SimpleMessageCard,
  ToolError,
  isBootsmanResult,
} from "./shared";
import { useFileLinkProps } from "./useFileLinkProps";

type TypecheckResult = {
  path: string;
  errorCount: number;
  errors: Array<{
    message: string;
    severity: string;
    location?: string;
  }>;
};

interface TypecheckProps {
  messageId: string;
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

export function Typecheck({ part, messageId }: TypecheckProps) {
  const isError = part.status === "error";
  const path = part.args?.path as string;
  const chatId = useActiveChatId();

  // Extract typecheck result if available
  const typecheckResult = (() => {
    if (
      part.status === "complete" &&
      part.result &&
      isBootsmanResult(part.result) &&
      part.result.success
    ) {
      return part.result.data as TypecheckResult;
    }
    return null;
  })();

  const hasTypeErrors = typecheckResult && typecheckResult.errorCount > 0;

  const getStatusText = () => {
    if (part.status === "pending") {
      return "Checking types...";
    }

    if (isError) {
      return "Failed to typecheck";
    }

    if (typecheckResult) {
      if (typecheckResult.errorCount === 0) {
        return "No type errors";
      }

      return `${typecheckResult.errorCount} type error${typecheckResult.errorCount !== 1 ? "s" : ""}`;
    }

    return "Typechecked";
  };

  const linkProps = useFileLinkProps(chatId, messageId, path || "");

  // If no errors and successful, show simple inline status
  if (!hasTypeErrors && !isError) {
    return (
      <SimpleMessageCard
        status={part.status}
        icon={FileCode2}
        title={
          <div className="flex items-center gap-2">
            {getStatusText()}
            {path && <FileName filename={path} linkProps={linkProps} />}
          </div>
        }
      />
    );
  }

  // Show accordion for errors
  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={FileCode2}
      title={getStatusText()}
      additionalContent={
        path && (
          <FileName filename={path} linkProps={linkProps} variant="outline" />
        )
      }
      accordionValue="typecheck"
    >
      {/* Show error details for typecheck errors */}
      {typecheckResult && typecheckResult.errorCount > 0 && (
        <ul className="space-y-1 text-sm list-disc list-outside px-4 grid gap-1">
          {typecheckResult.errors.map((error, index) => (
            <li
              key={`${error.message}-${error.location || index}`}
              className="list-item"
            >
              <div className=" text-warning min-w-0">
                <div className="break-words">{error.message}</div>
                {error.location && (
                  <div className="text-muted-foreground mt-0.5">
                    {error.location}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Show tool execution error */}
      {isError && part.result ? <ToolError result={part.result} /> : null}
    </ExpandableMessageCard>
  );
}
